import { Field, useFormikContext } from "formik";
import ValidationError from "./ValidationError";
import { getAmountAsNumber } from "../../util/currency";
import TogglePlusMinus from "../TogglePlusMinus";
import { get } from "lodash";
import { IValidatedField } from "../../interfaces/form";

interface ICurrencyField extends IValidatedField {
  /**
   * The name of the field that holds value as a number.
   */
  amountName: string;
  /**
   * The name of the field that holds value for plus/minus toggle.
   */
  toggleMinus?: string;
  /**
   * The name of the currency, e.g. "kr", "€", "£", "$", etc.
   */
  currencyDelimination?: string;
}

/**
 * Formik Field component that handles currency input. the name prop is the name
 * of the field that holds the value as a string.
 *
 * OBS! Formik fields that use this component should be of type string (name prop),
 * and have a corresponding field of type number (`amountName` prop) that holds the
 * value as a number. Validation is done on the string field and should include a
 * test function that uses testBeloeb() found in `src/util/currency.ts` to validate
 * the value.
 */
export default function CurrencyField<T>(props: ICurrencyField) {
  const {
    additionalClasses,
    error,
    touched,
    amountName,
    toggleMinus,
    currencyDelimination = "kr",
    wrapperAdditionalClasses,
    ...rest
  } = props;
  const formik = useFormikContext<T>();
  const displayName = props.name;
  const val: string = get(formik.values, displayName);
  const currentError = get(formik.errors, displayName);

  //set value of amountName-field to a number value corresponding to the value of the currency input field
  const amount = getAmountAsNumber(val);
  if (!currentError && amount !== get(formik.values, amountName)) {
    formik.setFieldValue(amountName, amount);
  }

  return (
    <div
      className={`input-group position-relative${
        wrapperAdditionalClasses ? " " + wrapperAdditionalClasses : ""
      }`}
    >
      {toggleMinus ? <TogglePlusMinus name={toggleMinus} /> : ""}
      <Field
        placeholder="0,00"
        as="input"
        type="text"
        autoComplete="off"
        className={`form-control text-end${
          error && touched ? " is-invalid" : ""
        }${additionalClasses ? " " + additionalClasses : ""}`}
        {...rest}
        name={displayName}
        onBlur={(e) => {
          let newValue = e.target.value;
          if (e.target.value.indexOf(".") > -1) {
            newValue = e.target.value.replace(".", ",");
          }
          if (e.target.value && !currentError) {
            if (newValue.indexOf(",") > -1) {
              if (newValue.indexOf(",") === newValue.length - 2)
                newValue += "0";
              if (newValue.indexOf(",") === newValue.length - 1)
                newValue += "00";
            }
            if (newValue.indexOf(",") === -1) newValue += ",00";
            if (newValue !== e.target.value)
              formik.setFieldValue(displayName, newValue);
          }
          formik.handleBlur(e);
        }}
      />
      <span className="input-group-text">{currencyDelimination}</span>
      <ValidationError error={error} show={touched} />
    </div>
  );
}
